import { useEffect, useMemo } from 'react';
import {
  CustomViewFormConditionField,
  CustomViewFormConditionItem,
} from 'src/graphql/entities/custom-view/custom-view.types';
import {
  QueryOptions,
  SupportedActionQueries,
  useOptionsQueries,
} from 'src/pages/Settings/rules/use-rules-engine';

export type ConditionValueOption = { value: unknown; label?: string; id?: unknown };

export const resolveFieldOptions = (
  field: CustomViewFormConditionField | undefined,
  queryOptions: QueryOptions,
): readonly ConditionValueOption[] => {
  const data = field?.optionsQuery ? queryOptions[field.optionsQuery] : undefined;
  return data?.historicalOptions ?? data?.options ?? field?.fieldOptions ?? [];
};

export const mapValuesToOptions = (
  values: readonly { value: unknown }[],
  options: readonly ConditionValueOption[],
): ConditionValueOption[] =>
  values.map(
    (item) =>
      options.find((option) => String(option.value) === String(item.value)) ??
      (item as ConditionValueOption),
  );

export const useCustomViewFilterOptions = ({
  conditions,
  partnerId,
}: {
  partnerId?: number | null;
  conditions?: CustomViewFormConditionItem[];
}) => {
  const enabledQueries = useMemo(
    () =>
      new Set(
        conditions
          ?.filter((condition) => condition.field?.optionsQuery)
          .map((condition) => condition.field!.optionsQuery!),
      ),
    [conditions],
  );

  const queryOptions = useOptionsQueries({
    partnerId: partnerId ?? null,
    enabledQueries,
    area: 'custom_views',
  });

  useEffect(() => {
    // accumulate conditions for the same trigger function to make a single api call
    // for all of them reducing number of total queries
    const triggers = conditions?.reduce(
      (acc, condition) => {
        const queryKey = condition.field?.optionsQuery!;
        const optionQueries = queryOptions[queryKey];

        if (!optionQueries?.onInputChange) {
          return acc;
        }

        const inputValue = Array.isArray(condition.value)
          ? condition.value.map((i) => i.id).join(',')
          : condition.value;

        const value = (inputValue as { value: string })?.value || (inputValue as string);

        if (value) {
          if (acc[queryKey]) {
            acc[queryKey].push(value);
          } else {
            acc[queryKey] = [value];
          }
        }

        return acc;
      },
      {} as Record<SupportedActionQueries, string[]>,
    );

    const triggersList = Object.entries(triggers ?? {});

    if (!triggersList?.length) {
      return;
    }

    // trigger accumulated actions
    triggersList.forEach(([queryKey, values]) => {
      const optionQueries = queryOptions[queryKey as SupportedActionQueries];
      optionQueries?.onInputChange?.(values.join(','));
    });

    // we don't need to subscribe for queryOptions update
    // since used properties from it are stable, others must
    // not produce any side effects
  }, [conditions, queryOptions]);

  return queryOptions;
};
